import { useContext, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import DashboardLayout from './components/Common/DashboardLayout';
import { LAST_USED_ACCOUNT_STORAGE_KEY } from './constants/storage';
import { AccountContentContextProvider } from './contexts/AccountContentContext';
import { AdsAccountContextProvider } from './contexts/AdsAccountContext';
import { AuthContext } from './contexts/AuthContext';
import { DashboardContextProvider } from './contexts/DashboardContext';
import { DynamicPageContextProvider } from './contexts/DynamicPageContext';
import { OverviewContextProvider } from './contexts/OverviewContext';
import Brand from './screens/Brand';
import ComingSoon from './screens/ComingSoon';
import Content from './screens/Content';
import DynamicPages from './screens/DynamicPages';
import FacebookAds from './screens/FacebookAds';
import Forms from './screens/Forms';
import GoogleAds from './screens/GoogleAds';
import Leads from './screens/Leads';
import Login from './screens/Login';
import ManageDynamicPage from './screens/ManageDynamicPage/ManageDynamicPage';
import SearchCampaigns from './screens/SearchCampaigns';
import SocialCampaigns from './screens/SocialCampaigns';
import Workspaces from './screens/Workspaces';
import AdAccountsSettings from './screens/settings/AdAccountsSettings';
import Billing from './screens/settings/Billing';
import DomainsSettings from './screens/settings/DomainsSettings';
import GeneralSettings from './screens/settings/GeneralSettings';
import IntegrationsSettings from './screens/settings/IntegrationsSettings';
import MembersSettings from './screens/settings/MembersSettings';
import SettingsWrapper from './screens/settings/SettingsWrapper';
import { track } from './utils/analytics';

import { Account } from '@betterfunnel/types';
import './App.css';
import { AISuggestionsContextProvider } from './contexts/AISuggestionsContextProvider';
import { ExportAnalyticsContextProvider } from './contexts/ExportAnalyticsContext';
import { UserContext } from './contexts/UserContext';
import ManageDynamicPageAdsMap from './screens/ManageDynamicPage/ManageDynamicPageAdsMap';
import ManageDynamicPageAnalytics from './screens/ManageDynamicPage/ManageDynamicPageAnalytics';
import ManageDynamicPageDetails from './screens/ManageDynamicPage/ManageDynamicPageDetails';
import ManageDynamicPageLeads from './screens/ManageDynamicPage/ManageDynamicPageLeads';
import ManageDynamicPageSettings from './screens/ManageDynamicPage/ManageDynamicPageSettings';
import { ManageDynamicPageSettingsAdsAccountsConnections } from './screens/ManageDynamicPage/ManageDynamicPageSettings/ManageDynamicPageSettingsAdsAccountsConnections';
import { ManageDynamicPageSettingsCustomCode } from './screens/ManageDynamicPage/ManageDynamicPageSettings/ManageDynamicPageSettingsCustomCode';
import { ManageDynamicPageSettingsDesign } from './screens/ManageDynamicPage/ManageDynamicPageSettings/ManageDynamicPageSettingsDesign';
import { ManageDynamicPageSettingsGeneral } from './screens/ManageDynamicPage/ManageDynamicPageSettings/ManageDynamicPageSettingsGeneral';
import { ManageDynamicPageSettingsIntegrations } from './screens/ManageDynamicPage/ManageDynamicPageSettings/ManageDynamicPageSettingsIntegrations';
import { ManageDynamicPageSettingsSEO } from './screens/ManageDynamicPage/ManageDynamicPageSettings/ManageDynamicPageSettingsSEO';
import { ManageDynamicPageSettingsTracking } from './screens/ManageDynamicPage/ManageDynamicPageSettings/ManageDynamicPageSettingsTracking';
import Onboard from './screens/Onboard';

const ProtectedRoute = ({
  isAuthenticated,
  redirect,
  addGoTo,
}: {
  isAuthenticated: boolean | null;
  redirect?: string | (() => string);
  addGoTo?: boolean;
}) => {
  if (!isAuthenticated) {
    const params = [
      ...(window.location.pathname !== '/' && addGoTo
        ? ['?goto=', window.location.pathname]
        : []),
      window.location.search,
    ]
      .filter((a) => !!a)
      .filter((_, __, arr) => arr.length > 1)
      .join('');
    const redirectTo =
      !redirect || typeof redirect === 'string'
        ? redirect || '/login'
        : redirect();
    return <Navigate to={redirectTo + params} replace />;
  }

  return <Outlet />;
};

const ProtectedRouteWithAccount = ({
  isAuthenticated,
  redirect,
  addGoTo,
}: {
  isAuthenticated: (account?: Account) => boolean;
  redirect?: string | (() => string);
  addGoTo?: boolean;
}) => {
  const { selectedAccount } = useContext(UserContext);

  const _isAuthenticated = isAuthenticated(selectedAccount?.data());

  return (
    <ProtectedRoute
      isAuthenticated={_isAuthenticated}
      redirect={redirect}
      addGoTo={addGoTo}
    />
  );
};

function App() {
  const location = useLocation();

  const { isAuthenticated, isAllowed } = useContext(AuthContext);

  useEffect(() => {
    track('web_app_startup');
  }, []);

  useEffect(() => {
    // if (hotjar.initialized()) {
    //  hotjar.stateChange(location.pathname);
    // }
    // track('page_view', location.pathname);
  }, [location]);

  return (
    <div className="h-screen overflow-y-scroll">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route
          element={<ProtectedRoute addGoTo isAuthenticated={isAuthenticated} />}
        >
          <Route
            element={
              <ProtectedRoute
                redirect="/coming-soon"
                isAuthenticated={isAllowed}
              />
            }
          >
            <Route
              path=""
              element={
                localStorage.getItem(LAST_USED_ACCOUNT_STORAGE_KEY) ? (
                  <Navigate
                    to={`/workspace/${localStorage.getItem(LAST_USED_ACCOUNT_STORAGE_KEY)}`}
                  />
                ) : (
                  <Navigate to="/workspaces" />
                )
              }
            />
            <Route path="/workspaces" element={<Workspaces />} />
            <Route
              path="/workspace/:slug"
              element={
                <DashboardContextProvider>
                  <OverviewContextProvider>
                    <AISuggestionsContextProvider>
                      <ExportAnalyticsContextProvider>
                        <DashboardLayout />
                      </ExportAnalyticsContextProvider>
                    </AISuggestionsContextProvider>
                  </OverviewContextProvider>
                </DashboardContextProvider>
              }
            >
              <Route path="onboard" element={<Onboard />} />

              <Route
                element={
                  <ProtectedRouteWithAccount
                    redirect="onboard"
                    isAuthenticated={(account) =>
                      account?.onboardStatus !== 'incomplete'
                    }
                  />
                }
              >
                {/* <Route path="overview" element={<Overview />} /> */}
                <Route path="overview" element={<Navigate to="../pages" />} />
                <Route path="campaigns" element={<Navigate to="../pages" />} />
                <Route path="pages" element={<DynamicPages />} />
                <Route
                  path="pages/:dynamicPageId"
                  element={
                    <DynamicPageContextProvider>
                      <ManageDynamicPage />
                    </DynamicPageContextProvider>
                  }
                >
                  <Route
                    path="details"
                    element={<ManageDynamicPageDetails />}
                  />
                  <Route path="adMap" element={<ManageDynamicPageAdsMap />} />
                  <Route path="leads" element={<ManageDynamicPageLeads />} />
                  <Route
                    path="analytics"
                    element={<ManageDynamicPageAnalytics />}
                  />
                  <Route
                    path="settings"
                    element={<ManageDynamicPageSettings />}
                  >
                    <Route
                      path="general"
                      element={<ManageDynamicPageSettingsGeneral />}
                    />
                    <Route
                      path="tracking"
                      element={<ManageDynamicPageSettingsTracking />}
                    />
                    <Route
                      path="ad-account"
                      element={
                        <ManageDynamicPageSettingsAdsAccountsConnections />
                      }
                    />
                    <Route
                      path="custom-code"
                      element={<ManageDynamicPageSettingsCustomCode />}
                    />
                    <Route
                      path="seo"
                      element={<ManageDynamicPageSettingsSEO />}
                    />
                    <Route
                      path="design"
                      element={<ManageDynamicPageSettingsDesign />}
                    />
                    <Route
                      path="integrations"
                      element={<ManageDynamicPageSettingsIntegrations />}
                    />
                    <Route
                      path=""
                      element={<Navigate replace to="general" />}
                    />
                    <Route
                      path="*"
                      element={<Navigate replace to="general" />}
                    />
                  </Route>

                  <Route path="*" element={<Navigate replace to="details" />} />
                  <Route path="" element={<Navigate replace to="details" />} />
                </Route>
                <Route path="search-campaigns" element={<SearchCampaigns />} />
                <Route path="social-campaigns" element={<SocialCampaigns />} />
                <Route
                  path="google-ads"
                  element={
                    <AdsAccountContextProvider key="google" provider="google">
                      <GoogleAds />
                    </AdsAccountContextProvider>
                  }
                />
                <Route
                  path="facebook-ads"
                  element={
                    <AdsAccountContextProvider
                      key="facebook"
                      provider={'facebook' as any}
                    >
                      <FacebookAds />
                    </AdsAccountContextProvider>
                  }
                />
                <Route path="leads" element={<Leads />} />
                <Route path="settings" element={<SettingsWrapper />}>
                  <Route path="general" element={<GeneralSettings />} />
                  <Route path="ad-accounts" element={<AdAccountsSettings />} />
                  <Route path="domains" element={<DomainsSettings />} />
                  <Route path="billing" element={<Billing />} />
                  <Route path="members" element={<MembersSettings />} />
                  <Route
                    path="integrations"
                    element={<IntegrationsSettings />}
                  />
                  <Route path="brand" element={<Brand />} />
                  <Route path="forms" element={<Forms />} />
                  <Route
                    path="content"
                    element={
                      <AccountContentContextProvider>
                        <Content />
                      </AccountContentContextProvider>
                    }
                  />
                  <Route path="" element={<Navigate replace to="general" />} />
                </Route>
                <Route path="*" element={<Navigate replace to="pages" />} />
                <Route path="" element={<Navigate replace to="pages" />} />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate replace to="" />} />
      </Routes>
    </div>
  );
}

export default App;
